<template>
  <div class="user-profile">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>个人资料</h2>
      <p>管理您的账户信息和设置</p>
    </div>

    <el-row :gutter="24">
      <!-- 左侧：个人信息 -->
      <el-col :xs="24" :lg="16">
        <!-- 基本信息 -->
        <el-card class="info-card">
          <template #header>
            <div class="card-header">
              <span>基本信息</span>
              <el-button
                  type="primary"
                  size="small"
                  @click="editBasicInfo"
              >
                <el-icon>
                  <Edit/>
                </el-icon>
                编辑
              </el-button>
            </div>
          </template>

          <el-descriptions :column="2" border>
            <el-descriptions-item label="用户名">
              {{ user.username }}
            </el-descriptions-item>
            <el-descriptions-item label="邮箱">
              {{ user.email }}
            </el-descriptions-item>
            <el-descriptions-item label="姓名">
              {{ user.full_name || '未设置' }}
            </el-descriptions-item>
            <el-descriptions-item label="手机号">
              {{ user.phone || '未设置' }}
            </el-descriptions-item>
            <el-descriptions-item label="部门">
              {{ user.department || '未设置' }}
            </el-descriptions-item>
            <el-descriptions-item label="角色">
              <el-tag :type="getRoleType(user.role)" size="small">
                {{ getRoleText(user.role) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="账户状态">
              <el-tag :type="user.is_active ? 'success' : 'danger'" size="small">
                {{ user.is_active ? '启用' : '禁用' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="最后登录">
              {{ user.last_login ? formatDateTime(user.last_login) : '从未登录' }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 数据范围 -->
        <el-card class="scope-card" v-if="user.data_scopes && user.data_scopes.length > 0">
          <template #header>
            <div class="card-header">
              <span>数据范围权限</span>
            </div>
          </template>

          <el-table :data="user.data_scopes" style="width: 100%">
            <el-table-column prop="environment" label="环境" width="120"/>
            <el-table-column prop="tenant_version" label="租户版本" width="120"/>
            <el-table-column prop="description" label="描述"/>
            <el-table-column label="创建时间" width="160">
              <template #default="{ row }">
                {{ formatDate(row.created_at) }}
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>

      <!-- 右侧：头像和操作 -->
      <el-col :xs="24" :lg="8">
        <!-- 头像 -->
        <el-card class="avatar-card">
          <template #header>
            <span>头像</span>
          </template>

          <div class="avatar-section">
            <el-avatar :size="120" :src="avatarUrl" class="user-avatar">
              {{ userInitials }}
            </el-avatar>

            <div class="avatar-actions">
              <el-upload
                  class="avatar-uploader"
                  action="#"
                  :show-file-list="false"
                  :before-upload="beforeAvatarUpload"
                  :http-request="uploadAvatar"
              >
                <el-button>
                  <el-icon>
                    <Camera/>
                  </el-icon>
                  更换头像
                </el-button>
              </el-upload>
              <el-button type="danger" @click="removeAvatar" v-if="avatarUrl">
                <el-icon>
                  <Delete/>
                </el-icon>
                移除头像
              </el-button>
            </div>

            <div class="avatar-tips">
              <p>支持 JPG、PNG 格式，大小不超过 2MB</p>
            </div>
          </div>
        </el-card>

        <!-- 安全设置 -->
        <el-card class="security-card">
          <template #header>
            <span>安全设置</span>
          </template>

          <div class="security-actions">
            <el-button @click="changePassword" class="security-button">
              <el-icon>
                <Lock/>
              </el-icon>
              修改密码
            </el-button>

            <el-button @click="enable2FA" class="security-button">
              <el-icon>
                <Key/>
              </el-icon>
              两步验证设置
            </el-button>

            <el-button @click="viewSessions" class="security-button">
              <el-icon>
                <Monitor/>
              </el-icon>
              登录设备管理
            </el-button>
          </div>
        </el-card>

        <!-- 统计信息 -->
        <el-card class="stats-card" v-if="userStats">
          <template #header>
            <span>个人工单统计</span>
            <span class="stats-period">({{ statsPeriod.start_date }} 至 {{ statsPeriod.end_date }})</span>
          </template>

          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-value">{{ userStats.created_orders || 0 }}</div>
              <div class="stat-label">创建的工单</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ userStats.handled_orders || 0 }}</div>
              <div class="stat-label">处理的工单</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ userStats.total_messages || 0 }}</div>
              <div class="stat-label">发送的消息</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ userStats.active_days || 0 }}</div>
              <div class="stat-label">活跃天数</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ userStats.avg_resolve_time_human || '0:00:00' }}</div>
              <div class="stat-label">平均解决时间</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ userStats.resolved_orders_count || 0 }}</div>
              <div class="stat-label">已解决工单</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 编辑信息对话框 -->
    <el-dialog
        v-model="editDialogVisible"
        title="编辑个人信息"
        width="500px"
    >
      <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="80px">
        <el-form-item label="姓名" prop="full_name">
          <el-input v-model="editForm.full_name" placeholder="请输入姓名"/>
        </el-form-item>

        <el-form-item label="邮箱" prop="email">
          <el-input v-model="editForm.email" placeholder="请输入邮箱"/>
        </el-form-item>

        <el-form-item label="手机号" prop="phone">
          <el-input v-model="editForm.phone" placeholder="请输入手机号"/>
        </el-form-item>

        <el-form-item label="部门" prop="department">
          <el-input v-model="editForm.department" placeholder="请输入部门"/>
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveBasicInfo" :loading="saving">
            保存
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 修改密码对话框 -->
    <el-dialog
        v-model="passwordDialogVisible"
        title="修改密码"
        width="500px"
    >
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef" label-width="100px">
        <el-form-item label="当前密码" prop="current_password">
          <el-input
              v-model="passwordForm.current_password"
              type="password"
              placeholder="请输入当前密码"
              show-password
          />
        </el-form-item>

        <el-form-item label="新密码" prop="new_password">
          <el-input
              v-model="passwordForm.new_password"
              type="password"
              placeholder="请输入新密码"
              show-password
          />
        </el-form-item>

        <el-form-item label="确认密码" prop="confirm_password">
          <el-input
              v-model="passwordForm.confirm_password"
              type="password"
              placeholder="请再次输入新密码"
              show-password
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="passwordDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="savePassword" :loading="changingPassword">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 两步验证设置对话框 -->
    <TwoFactorSettings v-model="twoFactorDialogVisible"/>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue'
import {ElMessage, ElMessageBox} from 'element-plus'
import {useAuthStore} from '@/stores/auth'
import dayjs from 'dayjs'
import {Camera, Delete, Edit, Key, Lock, Monitor} from '@element-plus/icons-vue'
import {getUserPersonalStats, updateAvatar} from '@/api/user'
import TwoFactorSettings from '@/components/common/TwoFactorSettings.vue'
import {useRouter} from "vue-router";
import {chunkedUploadToCos, getOssToken, uploadToCos} from "@/api/upload";
// import {resetSelfPassword} from "@/api/auth"; // 假设有这个API

const authStore = useAuthStore()
const router = useRouter()

// 响应式数据
const user = computed(() => authStore.user || {})
const userStats = ref({})
const editDialogVisible = ref(false)
const passwordDialogVisible = ref(false)
const saving = ref(false)
const changingPassword = ref(false)
const editFormRef = ref(null)
const passwordFormRef = ref(null)
const uploadingAvatar = ref(false)
const twoFactorDialogVisible = ref(false)
const statsPeriod = ref({
  start_date: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
  end_date: dayjs().format('YYYY-MM-DD')
})

// 计算属性 - 更新avatarUrl
const avatarUrl = computed(() => {
  return user.value?.avatar_url || ''
})

// 表单数据
const editForm = ref({
  full_name: '',
  phone: '',
  department: ''
})

const passwordForm = ref({
  current_password: '',
  new_password: '',
  confirm_password: ''
})

// 计算属性
const userInitials = computed(() => {
  const name = user.value.full_name || user.value.username || 'U'
  return name.charAt(0).toUpperCase()
})

const beforeAvatarUpload = (file) => {
  const isJPGOrPNG = file.type === 'image/jpeg' || file.type === 'image/png'
  const isLt2M = file.size / 1024 / 1024 < 2

  if (!isJPGOrPNG) {
    ElMessage.error('头像图片只能是 JPG/PNG 格式!')
  }
  if (!isLt2M) {
    ElMessage.error('头像图片大小不能超过 2MB!')
  }

  return isJPGOrPNG && isLt2M
}

// 验证规则
const editRules = {
  phone: [
    {pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur'}
  ]
}

const passwordRules = {
  current_password: [
    {required: true, message: '请输入当前密码', trigger: 'blur'}
  ],
  new_password: [
    {required: true, message: '请输入新密码', trigger: 'blur'},
    {min: 8, message: '密码长度至少8位', trigger: 'blur'},
    {
      validator: (rule, value, callback) => {
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(value)) {
          callback(new Error('密码必须包含大小写字母、数字和特殊字符'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ],
  confirm_password: [
    {required: true, message: '请确认密码', trigger: 'blur'},
    {
      validator: (rule, value, callback) => {
        if (value !== passwordForm.value.new_password) {
          callback(new Error('两次输入的密码不一致'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ]
}

// 方法
const uploadAvatar = async ({ file }) => {
  try {
    uploadingAvatar.value = true

    // 直接使用 handleFileChange 函数，但需要修改参数
    const tokenResponse = await getOssToken()
    const ossConfig = tokenResponse.data || tokenResponse

    // 上传到COS，使用不同的路径前缀
    let fileUrl
    const avatarPrefix = 'avatar/' // 头像使用单独的路径

    // 获取原始文件名
    const originalFileName = file.name
    console.log('开始上传头像:', originalFileName, '大小:', file.size)

    if (file.size > 100 * 1024 * 1024) {
      // 超过100MB，使用分片上传（头像一般不会这么大，但保留逻辑）
      console.log('头像文件超过100MB，使用分片上传')
      fileUrl = await chunkedUploadToCos(file, ossConfig, avatarPrefix)
    } else {
      // 小于100MB，使用普通上传
      console.log('头像文件小于100MB，使用普通上传')
      fileUrl = await uploadToCos(file, ossConfig, avatarPrefix)
    }

    console.log('头像上传成功，URL:', fileUrl)
    ElMessage.success(`${originalFileName} 上传成功`)

    // 调用头像更新接口，传递URL
    const updateResponse = await updateAvatar({
      avatar_url: fileUrl
    })

    console.log('头像信息更新到数据库成功:', updateResponse)

    // 更新本地用户信息
    authStore.setUser({
      ...authStore.user,
      avatar_url: fileUrl
    })

    ElMessage.success('头像更新成功')

  } catch (error) {
    console.error('上传头像失败:', error)
    ElMessage.error(error.response?.data?.message || '上传头像失败')
  } finally {
    uploadingAvatar.value = false
  }
}

// 移除头像
const removeAvatar = async () => {
  try {
    await ElMessageBox.confirm('确定要移除头像吗？', '确认移除', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })

    await removeAvatar()

    // 更新用户信息
    authStore.setUser({
      ...authStore.user,
      avatar_url: null
    })

    ElMessage.success('头像已移除')
  } catch (error) {
    if (error !== 'cancel') {
      console.error('移除头像失败:', error)
      ElMessage.error(error.response?.data?.message || '移除头像失败')
    }
  }
}

// 加载个人统计信息
const loadPersonalStats = async () => {
  try {
    const params = {
      start_date: statsPeriod.value.start_date,
      end_date: statsPeriod.value.end_date
    }

    const response = await getUserPersonalStats(params)

    if (response.data && response.data.statistics) {
      userStats.value = response.data.statistics
    }
  } catch (error) {
    console.error('加载个人统计失败:', error)
  }
}

const getRoleType = (role) => {
  const types = {
    admin: 'danger',
    manager: 'warning',
    user: 'success'
  }
  return types[role] || 'info'
}

const getRoleText = (role) => {
  const texts = {
    admin: '管理员',
    manager: '经理',
    user: '用户'
  }
  return texts[role] || role
}

const formatDate = (date) => {
  if (!date) return ''
  return dayjs(date).format('YYYY-MM-DD')
}

const formatDateTime = (date) => {
  if (!date) return ''
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
}

const editBasicInfo = () => {
  editForm.value = {
    full_name: user.value.full_name || '',
    phone: user.value.phone || '',
    department: user.value.department || ''
  }
  editDialogVisible.value = true
}

const saveBasicInfo = async () => {
  if (!editFormRef.value) return

  try {
    await editFormRef.value.validate()
    saving.value = true

    console.log('准备更新个人信息:', editForm.value)

    // 调用 store 中的 updateProfile 方法
    const result = await authStore.updateProfile(editForm.value)
    console.log('更新结果:', result)

    editDialogVisible.value = false
    ElMessage.success('个人信息更新成功')

    // 重新加载用户信息确保数据完整
    setTimeout(async () => {
      try {
        await authStore.fetchProfile()
        console.log('重新获取用户信息成功')
      } catch (error) {
        console.error('重新获取用户信息失败:', error)
      }
    }, 1000)

  } catch (error) {
    console.error('个人信息更新失败:', error)
    // 错误信息已经在 store 中处理
  } finally {
    saving.value = false
  }
}

const changePassword = () => {
  passwordForm.value = {
    current_password: '',
    new_password: '',
    confirm_password: ''
  }
  passwordDialogVisible.value = true
}

const savePassword = async () => {
  if (!passwordFormRef.value) return

  try {
    await passwordFormRef.value.validate()
    changingPassword.value = true

    await authStore.resetSelfPassword({
      current_password: passwordForm.value.current_password,
      new_password: passwordForm.value.new_password
    })

    passwordDialogVisible.value = false
    ElMessage.success('密码修改成功')

    // 清空表单
    passwordForm.value = {
      current_password: '',
      new_password: '',
      confirm_password: ''
    }

  } catch (error) {
    if (error.response?.data?.error) {
      ElMessage.error(error.response.data.error)
    }
  } finally {
    changingPassword.value = false
  }
}

const enable2FA = () => {
  twoFactorDialogVisible.value = true
}

const viewSessions = () => {
  ElMessage.info('登录设备管理功能开发中')
}

// 生命周期
onMounted(async () => {
  try {
    // 确保用户信息完整
    await authStore.ensureUserInfoComplete()
    // 然后加载统计信息
    await loadPersonalStats()
  } catch (error) {
    console.error('加载个人资料失败:', error)
    ElMessage.error('加载个人资料失败')
  }
})
</script>

<style scoped lang="scss">

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  .stat-item {
    background: var(--bg-base);
    border-radius: 8px;
    padding: 16px;
    text-align: center;
    transition: all var(--transition-base);

    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
    }

    .stat-value {
      font-size: 24px;
      font-weight: 600;
      color: var(--app-primary);
      margin-bottom: 4px;
    }

    .stat-label {
      font-size: 12px;
      color: var(--text-secondary);
    }
  }
}

.avatar-uploader {
  display: inline-block;
  margin-right: 10px;
}
.stats-period {
  font-size: 12px;
  color: var(--text-secondary);
  margin-left: 10px;
}
.user-profile {
  padding: 20px;

  .page-header {
    margin-bottom: 30px;

    h2 {
      margin: 0 0 8px 0;
      font-size: 28px;
      font-weight: 600;
      color: var(--text-primary);
    }

    p {
      margin: 0;
      color: var(--text-regular);
    }
  }

  .info-card,
  .scope-card,
  .avatar-card,
  .security-card,
  .stats-card {
    margin-bottom: 24px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .avatar-section {
    display: flex;
    flex-direction: column;
    align-items: center;

    .user-avatar {
      margin-bottom: 20px;
      border: 4px solid var(--border-lighter);
    }

    .avatar-actions {
      margin-bottom: 16px;
      display: flex;
      gap: 12px;

      .el-button {
        .el-icon {
          margin-right: 6px;
        }
      }
    }

    .avatar-tips {
      p {
        margin: 0;
        font-size: 12px;
        color: var(--text-secondary);
        text-align: center;
      }
    }
  }

  .security-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .security-button {
      width: 100%;
      justify-content: flex-start;
      padding: 12px 20px;
      margin: 0 !important;

      .el-icon {
        margin-right: 8px;
        width: 20px;
      }
    }
  }


  .stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    .stat-item {
      background: var(--bg-base);
      border-radius: 8px;
      padding: 16px;
      text-align: center;
      transition: all var(--transition-base);

      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
      }

      .stat-value {
        font-size: 24px;
        font-weight: 600;
        color: var(--app-primary);
        margin-bottom: 4px;
      }

      .stat-label {
        font-size: 12px;
        color: var(--text-secondary);
      }
    }
  }
}
</style>